import React from 'react';
import { HeartPulse, Plus, ShieldAlert } from 'lucide-react';

const severityTone = (severity) => {
  if (severity === 'minor') return 'bg-lime-50 text-lime-700 ring-lime-100';
  if (severity === 'moderate') return 'bg-amber-50 text-amber-700 ring-amber-100';
  return 'bg-rose-50 text-rose-700 ring-rose-100';
};

const statusTone = (status) => {
  if (status === 'active') return 'bg-rose-50 text-rose-700 ring-rose-100';
  if (status === 'recovering') return 'bg-amber-50 text-amber-700 ring-amber-100';
  return 'bg-emerald-50 text-emerald-700 ring-emerald-100';
};

const BodyAreaChips = ({ bodyPart }) => (
  <div className="flex flex-wrap gap-2">
    {String(bodyPart || '')
      .split(/[,;/]/)
      .map((part) => part.trim())
      .filter(Boolean)
      .slice(0, 4)
      .map((part) => (
        <span key={part} className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-black text-zinc-700 ring-1 ring-zinc-200">
          {part}
        </span>
      ))}
  </div>
);

const InjuryCard = ({ injury, t, locale }) => (
  <article className="rounded-lg border border-zinc-200 border-l-4 border-l-rose-300 bg-white p-4 shadow-sm" data-testid={`injury-row-${injury.id}`}>
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap gap-2">
          <span className={`rounded-full px-3 py-1 text-xs font-black ring-1 ${statusTone(injury.status)}`}>
            {t(`fitness.injuryStatus.${injury.status}`, injury.status)}
          </span>
          <span className={`rounded-full px-3 py-1 text-xs font-black ring-1 ${severityTone(injury.severity)}`}>
            {t(`fitness.severities.${injury.severity}`, injury.severity)}
          </span>
        </div>
        <h3 className="mt-3 text-lg font-black text-zinc-950">{injury.injury_type}</h3>
        <p className="mt-1 text-sm text-zinc-500">
          {t('fitness.injuryDate')}: {new Date(injury.date_occurred).toLocaleDateString(locale)}
        </p>
      </div>
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-700 ring-1 ring-rose-100">
        <ShieldAlert size={22} strokeWidth={1.8} />
      </div>
    </div>
    <div className="mt-4">
      <p className="mb-2 text-xs font-black uppercase tracking-[0.12em] text-zinc-400">{t('fitness.bodyPart')}</p>
      <BodyAreaChips bodyPart={injury.body_part} />
    </div>
    {injury.treatment ? (
      <p className="mt-4 rounded-lg bg-zinc-50 p-3 text-sm leading-6 text-zinc-600">
        <span className="font-black text-zinc-950">{t('fitness.treatment')}: </span>
        {injury.treatment}
      </p>
    ) : null}
    {injury.expected_recovery ? (
      <p className="mt-3 text-sm text-zinc-500">
        {t('fitness.expectedRecovery')}: <span className="font-black text-zinc-950">{new Date(injury.expected_recovery).toLocaleDateString(locale)}</span>
      </p>
    ) : null}
    {injury.notes ? <p className="mt-3 text-sm leading-6 text-zinc-500">{injury.notes}</p> : null}
  </article>
);

const InjuriesTab = ({ injuries, onAdd, t, locale }) => {
  const active = injuries.filter((injury) => injury.status !== 'recovered');

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-lg border border-zinc-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">{t('fitness.tabInjuries')}</p>
          <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">
            {active.length} {t('fitness.injuryStatus.active')}
          </h2>
          <p className="mt-1 text-sm leading-6 text-zinc-500">
            {t('fitness.injuryHint')}
          </p>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200"
          data-testid="injury-add-button"
        >
          <Plus size={16} strokeWidth={1.8} />
          {t('fitness.logInjury')}
        </button>
      </div>

      {injuries.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 bg-white p-8 text-center" data-testid="injury-empty">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
            <HeartPulse size={26} strokeWidth={1.8} />
          </div>
          <h2 className="mt-4 text-2xl font-black tracking-normal text-zinc-950">
            {t('common.noData')}
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
            {t('fitness.noActiveInjuries')}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {injuries.map((injury) => (
            <InjuryCard key={injury.id} injury={injury} t={t} locale={locale} />
          ))}
        </div>
      )}
    </div>
  );
};

export default InjuriesTab;
